import { useFeatureFlag } from "configcat-react";
import { WorkPacketType } from "./WorkPacketType";

type VisibilityFlagStream = "SHORTAGES" | "ACCRUALS" | "CHARGEBACKS";
export type VisibilityFlagView = "TRIGGER" | "PRE_CASE" | "ESCALATION" | "SETTLEMENT";
export type VisibilityFlagFeature = "COMMAND_CENTER" | "SUMMARY";
export type VisibilityFlag = `${VisibilityFlagStream}_${VisibilityFlagView}_${VisibilityFlagFeature}`;

const workPacketTypeToVisibilityFlagStreamMap: Record<WorkPacketType, VisibilityFlagStream> = {
  [WorkPacketType.SHORTAGES]: "SHORTAGES",
  [WorkPacketType.ACCRUALS]: "ACCRUALS",
  [WorkPacketType.CHARGEBACKS]: "CHARGEBACKS",
};

// Keep in sync with feature flags in ConfigCat
const existingVisibilityFeatureFlags = new Set<VisibilityFlag>([
  "SHORTAGES_TRIGGER_COMMAND_CENTER",
  "SHORTAGES_TRIGGER_SUMMARY",
  "SHORTAGES_PRE_CASE_SUMMARY",
  "SHORTAGES_ESCALATION_COMMAND_CENTER",
  "SHORTAGES_ESCALATION_SUMMARY",
  "SHORTAGES_SETTLEMENT_COMMAND_CENTER",
  "SHORTAGES_SETTLEMENT_SUMMARY",
  "ACCRUALS_TRIGGER_COMMAND_CENTER",
  "ACCRUALS_TRIGGER_SUMMARY",
  "CHARGEBACKS_TRIGGER_COMMAND_CENTER",
  "CHARGEBACKS_TRIGGER_SUMMARY",
]);

const defaults: Record<VisibilityFlagFeature, Record<VisibilityFlagView, boolean>> = {
  COMMAND_CENTER: {
    TRIGGER: false,
    PRE_CASE: false,
    ESCALATION: false,
    SETTLEMENT: false,
  },
  SUMMARY: {
    TRIGGER: false,
    PRE_CASE: false,
    ESCALATION: false,
    SETTLEMENT: false,
  },
};

function getDefaultValue(feature: VisibilityFlagFeature, view: VisibilityFlagView) {
  return defaults[feature]?.[view] ?? false;
}

export function useFeatureFlaggedVisibility(
  workPacketType: WorkPacketType,
  view: VisibilityFlagView,
  feature: VisibilityFlagFeature,
) {
  const stream = workPacketTypeToVisibilityFlagStreamMap[workPacketType];
  const flagName: VisibilityFlag = `${stream}_${view}_${feature}` as VisibilityFlag;

  // React hooks must be called unconditionally - use dummy flag if flag doesn't exist
  const hasCorrespondingFlag = existingVisibilityFeatureFlags.has(flagName);
  const fallbackFlagName = "SHORTAGES_TRIGGER_COMMAND_CENTER" as VisibilityFlag;

  const defaultValue = getDefaultValue(feature, view);
  const featureFlagResult = useFeatureFlag(hasCorrespondingFlag ? flagName : fallbackFlagName, defaultValue);
  return hasCorrespondingFlag ? featureFlagResult.value : defaultValue;
}
